import { createContext, useContext, useEffect, useState } from "react";

/**
 * Appearance modes — background image + subtle ambient accent lighting only.
 * Everything else (layout, typography, components, contrast, functionality)
 * is preserved across modes.
 */
export const APPEARANCE_MODES = [
  {
    id: "sierra-dawn",
    label: "Sierra Dawn",
    description: "Warm sunrise · blue-gray mountains",
    swatch:
      "linear-gradient(135deg, #f4c39b 0%, #b78a90 45%, #4c6a94 100%)",
  },
  {
    id: "alpine-blue",
    label: "Alpine Blue",
    description: "Cool alpine · deep navy glass",
    swatch:
      "linear-gradient(135deg, #a4c3e3 0%, #4c7bb8 55%, #1a2c4e 100%)",
  },
  {
    id: "mist",
    label: "Mist",
    description: "Pale gray-blue · calm neutral",
    swatch:
      "linear-gradient(135deg, #d6dde5 0%, #93a3b6 50%, #556877 100%)",
  },
  {
    id: "twilight",
    label: "Twilight",
    description: "Indigo dusk · violet horizon",
    swatch:
      "linear-gradient(135deg, #6b5b9a 0%, #3d3a6b 50%, #17182f 100%)",
  },
];

const STORAGE_KEY = "nn-appearance-mode";
const DEFAULT_MODE = "sierra-dawn";

const AppearanceContext = createContext({
  mode: DEFAULT_MODE,
  setMode: () => {},
});

export function AppearanceProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && APPEARANCE_MODES.some((m) => m.id === stored)) return stored;
    } catch (e) { /* localStorage unavailable */ }
    return DEFAULT_MODE;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) { /* ignore */ }
  }, [mode]);

  return (
    <AppearanceContext.Provider value={{ mode, setMode }}>
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance() {
  return useContext(AppearanceContext);
}
